/**
 * Layer 2's places, shaped for the map: fetched, kept on this phone, scored, and turned into the
 * one collection the heat and the pins both draw from.
 *
 * The server sends no score (see the API's `pois/queries.ts`): how much a place is worth depends
 * on the hour and on where the reader is standing, so it is worked out here, from `poiScore`, the
 * same function the ranked list reads, so the heat and the list can never disagree.
 */
import type { Mode, SectorSnapshot } from "@trip/domain/danger";
import { foldName, poiScore, type PoiWindow } from "@trip/domain/poi";
import { useCallback, useEffect, useRef, useState } from "react";
import { del, get, patch, post } from "./api.ts";
import { DEFAULT_EMOJI, UNCATEGORISED, nextColour } from "./category-icons.ts";

/** One place as the API serves it (`GET /v1/trips/:tripId/pois`). */
export type Poi = {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /** Null when the place has no category. */
  categoryId: string | null;
  note: string | null;
  source: "MANUAL" | "IMPORT";
  externalId: string | null;
  addedBy: string;
  createdAt: string;
  /** Who said it was done, and when. A ticked place is no longer suggested. */
  ticked: { at: string; by: string } | null;
  windows: PoiWindow[];
  votes: number;
  /** Whether the reader is one of `votes`. */
  voted: boolean;
  /**
   * Who the `votes` are, by name, in the order they voted. Absent only from a list saved on this
   * phone before the server sent it; the place view then says how many, not who.
   */
  voters?: PoiVoter[];
  commentCount?: number;
};
/** One person who likes a place, by the name they go by now ("Former member" once they cannot be seen). */
export type PoiVoter = { userId: string; name: string };
/** One comment on a place (`GET /v1/trips/:tripId/pois/:poiId/comments`), oldest first. */
export type PoiComment = {
  id: string;
  userId: string;
  /** The writer's name as they go by it now. */
  name: string;
  body: string;
  createdAt: string;
};
/** One of the trip's categories (`GET /v1/trips/:tripId/poi-categories`), with its face. */
export type PoiCategory = {
  id: string;
  name: string;
  /** Lower-case `#rrggbb`: the pin's disc and the picked chip's outline. */
  color: string;
  /** What the pin and the chip show: 1 to 16 characters, usually one emoji. */
  emoji: string;
  createdBy: string;
  createdAt: string;
  /** Where it stands in the trip's order, from 0; null until someone has ordered the list. */
  position: number | null;
};

/** A place with what it is worth now, and each term of it, so the interface can say why. */
export type ScoredPoi = Poi & ReturnType<typeof poiScore>;

export type PoiFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    id: string;
    name: string;
    /**
     * The place's category, or null when it has none or has one this list does not know (removed
     * since): the pin reads its image from it, `cat-<id>`, or `cat-none`.
     */
    categoryId: string | null;
    /** The category's face, or the grey 📍 of a place with none. */
    emoji: string;
    color: string;
    /** What the heat reads: the score, against the best place on show. Above 0, at most 1. */
    weight: number;
    /** In no sector: no danger level to report, and the pin says so. */
    noData: boolean;
    /** What the pin prints. */
    label: string;
  };
};
export type PoiFeatures = { type: "FeatureCollection"; features: PoiFeature[] };

/** Where the pin of a place outside every sector says it has no level, rather than looking calm. */
export const NO_DATA_LABEL = "No safety data";

/**
 * Every place still to do, best first, with its score. A ticked place is left out: it has been
 * done, so it is neither heat nor a suggestion. So is a place whose coordinates are not numbers:
 * `poiScore` would measure no walk to it and call it right here, which is the one place it is not.
 *
 * `from` is where closeness is measured from; null takes no distance penalty for anybody.
 */
export function scoredPois(
  pois: Poi[],
  now: number,
  from: { lat: number; lon: number } | null,
  sectors: SectorSnapshot[],
  mode: Mode,
): ScoredPoi[] {
  return pois
    .filter((p) => p.ticked === null && Number.isFinite(p.lat) && Number.isFinite(p.lon))
    .map((p) => ({
      ...p,
      ...poiScore({
        lat: p.lat,
        lon: p.lon,
        fromLat: from?.lat ?? NaN,
        fromLon: from?.lon ?? NaN,
        votes: p.votes,
        windows: Array.isArray(p.windows) ? p.windows : [],
        nowMs: now,
        mode,
        sectors,
      }),
    }))
    .sort((a, b) => b.score - a.score);
}

/**
 * The heat's and the pins' one collection, narrowed to a category when one is picked. Built once
 * per change and handed to the map whole, as `toPointFeatures` is: sixty places are sixty
 * features in one `setData`, never a source or a call each.
 *
 * The heat is the score without its proximity term: √safety × wanted × urgency. The heat answers
 * "where do the places worth going cluster", and the traveller's own dot already says where they
 * are; with proximity in, everything more than a few minutes away faded to nothing and the map
 * showed one glow around the reader instead of the city's hot zones. The ranked list keeps the full
 * score, distance included.
 *
 * Read against the best on show, so the best zone is always the brightest. The heatmap wants
 * weights near 0..1. No term is ever zero (see `poiScore`), so nor is a weight.
 */
export function poiFeatures(
  scored: ScoredPoi[],
  category: string | null,
  categories: PoiCategory[],
): PoiFeatures {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const shown = category === null ? scored : scored.filter((p) => p.categoryId === category);
  // Safety enters the heat square-rooted: still a weight, so a level-6 street is dimmer than a
  // calm one, but no longer the term that decides the picture. Unsoftened, one place in a calm
  // sector outshone twenty in Roma (0.30 against 1.00 each), and the map hid the city's actual
  // clusters. The ranked list keeps the full term.
  const heatOf = (p: ScoredPoi) => Math.sqrt(p.safety) * p.wanted * p.urgency;
  const best = Math.max(0, ...shown.map(heatOf));
  return {
    type: "FeatureCollection",
    features: shown.map((p) => {
      const c = p.categoryId === null ? undefined : byId.get(p.categoryId);
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lon, p.lat] },
        properties: {
          id: p.id,
          name: p.name,
          categoryId: c ? c.id : null,
          emoji: c ? c.emoji : UNCATEGORISED.emoji,
          color: c ? c.color : UNCATEGORISED.color,
          weight: best > 0 ? heatOf(p) / best : 1,
          noData: p.level === null,
          label: p.level === null ? `${p.name}\n${NO_DATA_LABEL}` : p.name,
        },
      };
    }),
  };
}

// --- the list, kept on this phone -------------------------------------------------------------

/** The places whose name holds what was typed, by `foldName`, A to Z; nothing typed is nothing. */
export function placesNamed(pois: Poi[], query: string): Poi[] {
  const needle = foldName(query);
  if (needle === "") return [];
  return pois
    .filter((p) => foldName(p.name).includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** One entry per trip. Versioned, so a change of shape reads as nothing saved, not as garbage. */
export const poisCacheKey = (tripId: string) => `trip:pois:v2:${tripId}`;

type Saved = { pois: Poi[]; categories: PoiCategory[] };

/** The list as last fetched on this phone, or null when there is none or it cannot be read. */
function readSaved(tripId: string): Saved | null {
  try {
    const raw = localStorage.getItem(poisCacheKey(tripId));
    if (raw === null) return null;
    const s = JSON.parse(raw) as Partial<Saved>;
    return Array.isArray(s.pois) && Array.isArray(s.categories)
      ? {
          pois: s.pois,
          // Saved before categories had an order: the key is missing, not null.
          categories: s.categories.map((c) => ({ ...c, position: c.position ?? null })),
        }
      : null;
  } catch {
    return null;
  }
}

/** What the page says, word for word, when the list is not the fresh one or is not there at all. */
export const POIS_UNREACHABLE = "The places could not be loaded. Check your connection.";
export const POIS_STALE =
  "Could not reach the server: showing the places as last saved on this phone.";
export const POIS_UNSAVED =
  "These places could not be saved on this phone, so they will not be here offline.";

export type PoiList = {
  /** Null until there is a list to show: fetched, or saved on this phone from last time. */
  pois: Poi[] | null;
  categories: PoiCategory[] | null;
  /**
   * What went wrong, in words for the page, or null. With `pois` null it is the whole story (the
   * list could not be had); with a list it qualifies it (it is the saved one, or it could not be
   * saved for next time).
   */
  error: string | null;
};

const NOTHING: PoiList = { pois: null, categories: null, error: null };

/** The list, and how a write the server has confirmed is put into it. */
export type LivePoiList = PoiList & {
  /**
   * One place as the server now has it, after a tick, a vote, an edit or its creation: it
   * replaces the one in the list, or joins it when it is new, and the list is saved again. Only
   * ever called with the server's answer, never with a guess, so the list cannot show a write
   * that did not happen.
   */
  replace: (poi: Poi) => void;
  /**
   * A category as the server now has it, new, one the trip already had, or just edited: it
   * replaces the one with its id, or joins the list.
   */
  addCategory: (category: PoiCategory) => void;
  /** A place the server has deleted: out of the list, and kept out of a fetch already in flight. */
  remove: (id: string) => void;
  /**
   * A category the server has deleted: out of the list, and its places left without one, as the
   * server leaves them. Done at once, so a fetch after it that fails does not leave it on the
   * chips, in the editor or on the pins.
   */
  removeCategory: (id: string) => void;
  /**
   * Fetch the list again, as after a category is removed and its places change on the server.
   * The list on show stays up meanwhile; a fetch that fails leaves it there and says it may be
   * out of date.
   */
  refresh: () => void;
};

/** `items` with each of `over` put in place of the one with its id, or added when it has none. */
function upsert<T extends { id: string }>(items: T[], over: Iterable<T>): T[] {
  const out = [...items];
  for (const o of over) {
    const i = out.findIndex((x) => x.id === o.id);
    if (i >= 0) out[i] = o;
    else out.push(o);
  }
  return out;
}

/**
 * `saved` less what the server has deleted since: the places gone, the categories gone, and the
 * places that were in one of those left without a category, as the server leaves them.
 */
function withoutRemoved(saved: Saved, pois: Set<string>, categories: Set<string>): Saved {
  if (pois.size === 0 && categories.size === 0) return saved;
  return {
    pois: saved.pois
      .filter((p) => !pois.has(p.id))
      .map((p) =>
        p.categoryId !== null && categories.has(p.categoryId) ? { ...p, categoryId: null } : p,
      ),
    categories: saved.categories.filter((c) => !categories.has(c.id)),
  };
}

/**
 * The trip's places and categories. Nothing is fetched while `enabled` is false; while it is
 * true the saved list is shown at once and the fresh one replaces it when it lands, and is saved
 * in its turn. Layer 2 reads offline: the saved list is what it reads when there is no signal,
 * and the page is told that it is the saved one rather than left to present it as current.
 */
export function usePois(tripId: string, enabled: boolean): LivePoiList {
  const [list, setList] = useState<PoiList>(NOTHING);
  /** Bumped by `refresh`, which runs the fetch below again. */
  const [round, setRound] = useState(0);
  /** Set by `refresh`, so the fetch it runs keeps the list on show rather than the saved one. */
  const refreshing = useRef(false);
  /** Set by `replace`, so the save below follows a confirmed write and not every fetch. */
  const dirty = useRef(false);
  /**
   * What the server has confirmed since the fetch in flight was sent. That fetch can land after a
   * tick that went out later and came back sooner, carrying the place as it was before the tick;
   * taken whole, it would undo the tick on screen and in the saved copy. So its answer is read
   * with these laid over it.
   */
  const confirmed = useRef({
    pois: new Map<string, Poi>(),
    categories: new Map<string, PoiCategory>(),
    removedPois: new Set<string>(),
    removedCategories: new Set<string>(),
  });
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    confirmed.current = {
      pois: new Map(),
      categories: new Map(),
      removedPois: new Set(),
      removedCategories: new Set(),
    };
    const saved = readSaved(tripId);
    if (refreshing.current) refreshing.current = false;
    else setList(saved ? { ...saved, error: null } : NOTHING);
    const base = `/v1/trips/${tripId}`;
    Promise.all([
      get<{ items: Poi[] }>(`${base}/pois`),
      get<{ items: PoiCategory[] }>(`${base}/poi-categories`),
    ])
      .then(([p, c]) => {
        if (!live) return;
        const fresh = withoutRemoved(
          {
            pois: upsert(p.items, confirmed.current.pois.values()),
            categories: upsert(c.items, confirmed.current.categories.values()),
          },
          confirmed.current.removedPois,
          confirmed.current.removedCategories,
        );
        let error: string | null = null;
        try {
          localStorage.setItem(poisCacheKey(tripId), JSON.stringify(fresh));
        } catch (e) {
          // Said, not swallowed: a traveller who finds the list gone in a dead spot was owed this.
          console.error("places: the list could not be saved for offline use", e);
          error = POIS_UNSAVED;
        }
        setList({ ...fresh, error });
      })
      .catch((e: unknown) => {
        if (!live) return;
        console.error("places: the list could not be fetched", e);
        // The list on show is the saved one, or after a refresh the one already up: kept, and said.
        setList((l) =>
          l.pois !== null ? { ...l, error: POIS_STALE } : { ...NOTHING, error: POIS_UNREACHABLE },
        );
      });
    return () => {
      live = false;
    };
  }, [tripId, enabled, round]);
  useEffect(() => {
    if (!dirty.current || list.pois === null || list.categories === null) return;
    dirty.current = false;
    try {
      localStorage.setItem(
        poisCacheKey(tripId),
        JSON.stringify({ pois: list.pois, categories: list.categories }),
      );
    } catch (e) {
      console.error("places: the list could not be saved for offline use", e);
      setList((l) => ({ ...l, error: POIS_UNSAVED }));
    }
  }, [list, tripId]);
  const replace = useCallback((poi: Poi) => {
    dirty.current = true;
    confirmed.current.pois.set(poi.id, poi);
    setList((l) => (l.pois === null ? l : { ...l, pois: upsert(l.pois, [poi]) }));
  }, []);
  const addCategory = useCallback((category: PoiCategory) => {
    dirty.current = true;
    confirmed.current.categories.set(category.id, category);
    setList((l) =>
      l.categories === null ? l : { ...l, categories: upsert(l.categories, [category]) },
    );
  }, []);
  const remove = useCallback((id: string) => {
    dirty.current = true;
    confirmed.current.pois.delete(id);
    confirmed.current.removedPois.add(id);
    setList((l) => (l.pois === null ? l : { ...l, pois: l.pois.filter((p) => p.id !== id) }));
  }, []);
  const removeCategory = useCallback((id: string) => {
    dirty.current = true;
    confirmed.current.categories.delete(id);
    confirmed.current.removedCategories.add(id);
    setList((l) =>
      l.pois === null || l.categories === null
        ? l
        : {
            ...l,
            ...withoutRemoved({ pois: l.pois, categories: l.categories }, new Set(), new Set([id])),
          },
    );
  }, []);
  const refresh = useCallback(() => {
    refreshing.current = true;
    setRound((r) => r + 1);
  }, []);
  return {
    ...(enabled ? list : NOTHING),
    replace,
    addCategory,
    remove,
    removeCategory,
    refresh,
  };
}

// --- the saved list, synced from Google --------------------------------------------------------

/** What a sync did (`POST /v1/trips/:tripId/pois/sync`). */
export type PoiSyncResult = {
  added: number;
  /** Imported places the list no longer holds, removed from the trip. */
  removed: number;
  skipped: number;
  listName: string;
  /** Whether the link sent was kept for next time; only a trip admin's is. */
  linkSaved: boolean;
};

/**
 * The server's answer when the trip has no stored link and none was sent. Told apart from the
 * other refusals by its words alone, so the words come from the contract the API also reads.
 */
export { SYNC_NEEDS_LINK } from "@trip/contracts/messages";
/** What the control says when the request never got an answer. Nothing is queued for later. */
export const SYNC_OFFLINE = "Syncing needs the network. Connect and try again.";

/**
 * Make the trip's imported places match its Google Maps saved list: new ones added, ones the list
 * no longer holds removed, the rest left as they are; places added in the app are never touched.
 * With no `url` the server uses the link it has stored. `{}` rather than no body: a
 * JSON request with nothing in it is refused before the route is reached.
 */
export const syncPois = (tripId: string, url?: string) =>
  post<PoiSyncResult>(`/v1/trips/${tripId}/pois/sync`, url === undefined ? {} : { url });

/**
 * A sync's result, in the words the control shows: "Added 3 new places · removed 1 · 45 already
 * here", the removal left out when there was none.
 */
export function syncSaid(r: PoiSyncResult, sentLink: boolean): string {
  const parts = [`Added ${r.added} new ${r.added === 1 ? "place" : "places"}`];
  if (r.removed > 0) parts.push(`removed ${r.removed}`);
  parts.push(`${r.skipped} already here`);
  const did = parts.join(" · ");
  return sentLink && !r.linkSaved
    ? `${did}. Only the trip admin can save the link, so it will be asked for again next time.`
    : did;
}

// --- one place: removing it, and what is said about it ----------------------------------------

/** Remove a place, with its windows, votes, tick and comments. Its adder or a trip admin only. */
export const removePoi = (tripId: string, poiId: string) =>
  del<void>(`/v1/trips/${tripId}/pois/${poiId}`);

/**
 * An edit of a place, sent: the server's answer is the place as it now is. A name is sent only
 * when it changed, so an imported place's name is never rewritten by an edit of its hours, and a
 * note only when it changed, because the server fits a long name into it and it is not echoed back.
 * The map's editor and the Places page's both save through this.
 */
export const editPoi = (
  tripId: string,
  poi: Pick<Poi, "id" | "name" | "note">,
  draft: Pick<Poi, "categoryId" | "windows"> & { name: string; note: string | null },
) =>
  patch<Poi>(`/v1/trips/${tripId}/pois/${poi.id}`, {
    ...(draft.name !== poi.name ? { name: draft.name } : {}),
    ...(draft.note !== (poi.note ?? null) ? { note: draft.note } : {}),
    categoryId: draft.categoryId,
    windows: draft.windows,
  });

/**
 * A category for the trip from the place editor, where only its name is asked: it gets the plain
 * pin and the first colour no category wears yet. The server answers with the one it already had
 * under that name, which keeps its own face.
 */
export const addCategoryNamed = (
  tripId: string,
  name: string,
  existing: Pick<PoiCategory, "color">[],
) =>
  post<PoiCategory>(`/v1/trips/${tripId}/poi-categories`, {
    name,
    color: nextColour(existing),
    emoji: DEFAULT_EMOJI,
  });

/** A place's comments, oldest first. */
export const listComments = (tripId: string, poiId: string) =>
  get<{ items: PoiComment[] }>(`/v1/trips/${tripId}/pois/${poiId}/comments`).then((r) => r.items);

/** A comment by the reader, as the server stored it. */
export const addComment = (tripId: string, poiId: string, body: string) =>
  post<PoiComment>(`/v1/trips/${tripId}/pois/${poiId}/comments`, { body });

/**
 * Whether the reader may remove a place: whoever added it, or a trip admin. The server's own
 * rule, so nothing offers a removal it would refuse.
 */
export function canRemovePoi(poi: Pick<Poi, "addedBy">, me: string | null, isAdmin: boolean) {
  return isAdmin || (me !== null && poi.addedBy === me);
}
